import { startRemoteActivation } from "./activation";
import {
  controller,
  localMode,
  injectedSeed,
  noteVisibility,
  notePageShow,
} from "./runtime";
import { safely } from "./contracts";
import config, { getPlatform } from "../../utils/config";

import { SDK_VERSION, sdkCapabilities } from "./sdkCapabilities";

export function installRemoteActivation() {
  if (localMode) return;
  return safely(() => {
    const origin = window.location.origin;
    const environment =
      origin === "https://creator.wishlink.com"
        ? "production"
        : /^https:\/\/wishlink-creator-v2-(?:[1-9]|1[0-2])\.staging\.wishlink\.one$/.test(
            origin
          )
        ? "staging"
        : undefined;
    const apiOrigin = new URL(config.API.url).origin;
    if (!environment || !apiOrigin.startsWith("https://")) return;
    const platform = safely(() => getPlatform(), "web");
    const webRelease = process.env.REACT_APP_VERSION;
    if (!webRelease) return;
    const destinationReady =
      process.env.REACT_APP_OBSERVABILITY_EXPORTER_URL ===
        "https://applens.wishlink.com/v1/traces" &&
      process.env.REACT_APP_OBSERVABILITY_EXPORTER_HOST ===
        "applens.wishlink.com";
    if (!destinationReady || !SDK_VERSION) return;
    const capabilities = sdkCapabilities(SDK_VERSION);
    let pageHidden = false;
    return startRemoteActivation({
      apiOrigin,
      injectedSeed,
      scope: `${origin}|${apiOrigin}|${webRelease}`,
      identity: {
        appId: "creator",
        serviceName: "creator-web",
        environment,
        runtime: "web",
        hostPlatform:
          platform === "ios" || platform === "android" ? platform : "web",
        sdkVersion: SDK_VERSION,
        webRelease,
      },
      propagationAllowed:
        process.env.REACT_APP_OBSERVABILITY_PROPAGATION_ALLOWED === "true",
      sdkPropagationSupported: capabilities.deploymentPropagation,
      // Getters may throw in restricted WebViews; that is an ineligible installation.
      storage: {
        getItem: (key) => window.localStorage.getItem(key),
        setItem: (key, value) => window.localStorage.setItem(key, value),
        removeItem: (key) => window.localStorage.removeItem(key),
      },
      random: safely(
        () =>
          typeof window.crypto?.getRandomValues === "function"
            ? (bytes: Uint8Array) => {
                window.crypto.getRandomValues(bytes);
              }
            : undefined,
        undefined
      ),
      now: () => Date.now(),
      monotonicNow: () => performance.now(),
      visible: () => !pageHidden && document.visibilityState !== "hidden",
      fetch: (input, init) => window.fetch(input, init),
      abort: () => new AbortController(),
      schedule(ms, work) {
        const timer = setTimeout(work, ms);
        return () => clearTimeout(timer);
      },
      listen(work) {
        // Installed before the SDK's lifecycle listener; stale authority is revoked first.
        const hide = () => {
          pageHidden = true;
          work();
        };
        const show = (event: PageTransitionEvent) => {
          notePageShow(event.persisted);
          pageHidden = false;
          work();
        };
        const resume = () => {
          // Some WebViews resume without pageshow. Only a visible lifecycle event
          // clears pagehide; online alone must not reactivate a backgrounded page.
          if (document.visibilityState === "visible") pageHidden = false;
          work();
        };
        const visibility = () => {
          safely(
            () => noteVisibility(document.visibilityState !== "hidden"),
            undefined
          );
          resume();
        };
        document.addEventListener("visibilitychange", visibility);
        window.addEventListener("pageshow", show);
        window.addEventListener("pagehide", hide);
        window.addEventListener("online", work);
        document.addEventListener("resume", resume);
        return () => {
          document.removeEventListener("visibilitychange", visibility);
          window.removeEventListener("pageshow", show);
          window.removeEventListener("pagehide", hide);
          window.removeEventListener("online", work);
          document.removeEventListener("resume", resume);
        };
      },
      controller: {
        setEarlyCapturePolicy: controller.setEarlyCapturePolicy,
        resetForConfiguration: controller.resetForConfiguration,
        disable: () => controller.disable(),
        configureDeployment: (input) => controller.configureDeployment(input),
        ...(capabilities.renewLease
          ? { renewLease: controller.renewLease }
          : {}),
      },
    });
  }, undefined);
}
